import type {
  AttioMappingAction,
  AttioMappingProposal,
  DiscoveredObjectType,
} from './api';
import { ATTIO_OBJECT_TYPES } from './constants';

export const STEP_CONNECT = 'connect';
export const STEP_CONFIGURE = 'configure';
export const STEP_REVIEW = 'review';

export interface AttioImportWizardState {
  apiKey: string;
  connectError: string | null;
  selectedTypes: string[];
  discoveredTypes: DiscoveredObjectType[];
  /**
   * Which standard objects the connected workspace has, keyed by entity type
   * name — from discovery's `standard_object_types`. Null until discovery has
   * run (or when it failed / the backend predates the field): unknown
   * availability renders everything enabled, and the backend's own workspace
   * guard covers the rest.
   */
  standardAvailability: Record<string, boolean> | null;
  proposalsByEntity: Record<string, AttioMappingProposal[]> | null;
  proposalsLoadError: boolean;
  truncatedByEntity: Record<string, boolean> | null;
  totalRecordsByEntity: Record<string, number> | null;
  actionByKey: Record<string, AttioMappingAction>;
}

// Default configure-step selection: every supported object type. Hoisted so
// the initial state, reset, and the isDirty comparison all share one stable
// reference.
export const DEFAULT_SELECTED_TYPES = ATTIO_OBJECT_TYPES.map(type => type.name);

// The built-in (non-custom) object types. A selection in this set is always
// valid regardless of discovery; anything else is a discovered custom
// (`org_...`) type that only stays valid while it's in the current discovery
// result.
export const BUILTIN_TYPE_NAMES = new Set(DEFAULT_SELECTED_TYPES);

/**
 * Reconcile a prior selection against a freshly-discovered custom-type set and
 * the workspace's standard-object availability.
 *
 * A built-in selection is kept unless discovery said the workspace doesn't have
 * that object (`standardAvailability[name] === false` — deals is optional in
 * Attio); a null/absent availability map (discovery failed, or an older
 * backend) keeps every built-in, and the backend's workspace guard covers the
 * rest. A custom (`org_...`) selection is kept only if it's still in
 * `discoveredTypes` — so a re-run that drops a type, or a discovery failure
 * (empty set), removes stale custom selections that would otherwise submit
 * `org_...` names against the wrong Attio schema.
 */
export function reconcileSelectedTypes(
  prevSelected: string[],
  discoveredTypes: DiscoveredObjectType[],
  standardAvailability: Record<string, boolean> | null = null
): string[] {
  const discoveredNames = new Set(
    discoveredTypes.map(type => type.entity_type_name)
  );
  return prevSelected.filter(name => {
    if (BUILTIN_TYPE_NAMES.has(name)) {
      return standardAvailability?.[name] !== false;
    }
    return discoveredNames.has(name);
  });
}
